import { fromBase64, fromUtf8, utf8 } from './bytes';
import { open, seal } from './crypto';

/**
 * The server's picture of one vault, ported from
 * `shared/.../vault/cloud/CloudManifest.kt`.
 *
 * The envelope is plaintext — the server reads it to CAS on
 * `manifestVersion`. Everything that would name a file lives in the sealed
 * body: blob ids are one-way HMACs, so without `paths` a puller holding the
 * blobs still cannot tell what any of them is.
 *
 * `body` versus `bodyBlobId` is not a version flag. Firestore's 1 MiB
 * document limit rules out inlining the body at real vault sizes, so a live
 * envelope points at a Storage blob; the inline form is what small and older
 * vaults carry, and both shapes are current.
 */

const MANIFEST_AAD_V1 = utf8('pondering.manifest.v1');
const MANIFEST_AAD_V2 = utf8('pondering.manifest.v2');

/**
 * Which wire shape a body is sealed in. Bound by the body's AAD rather than a
 * field, which is the fence that made the shape change shippable: a build that
 * PARSED v2 with v1 eyes would read blob ids as paths, and its next push would
 * apply that as peer deletions. Being unable to open it is the point.
 *
 *  - v1 — `paths`: blobId → path; ids derived from the path, versions move on
 *    every edit, so an upload overwrites the key the manifest points at.
 *  - v2 — `files`: path → blobId; ids derived from the CONTENT, so an upload
 *    never overwrites a live key, a move is a manifest-only edit, and
 *    identical content dedups to one blob.
 */
export type CloudManifestFormat = 'v1' | 'v2';

export interface ManifestEnvelope {
  manifestVersion: number;
  deviceId: string;
  updatedAtMs: number;
  /** base64 of the masterKey-wrapped dataKey. A web session cannot unwrap
   *  it — it holds the dataKey directly — but it must echo this back
   *  verbatim on write or every keyed device is stranded. */
  wrappedDataKey: string;
  /** base64 of the sealed body, inline. Absent when it lives in a blob. */
  body?: string;
  /** Storage blob holding the sealed body. */
  bodyBlobId?: string;
  /** SHA-256 of the custodian's access proof. Absent on a vault no updated
   *  custodian has pushed to yet; immutable once set, and a grant writer
   *  must echo it back exactly or the push is refused. */
  accessAuthHash?: string;
  serverUpdatedAtMs?: number;
  /** The version the body holds, when the snapshot is written only now and
   *  then and each version after it is its record alone: the vault is the
   *  snapshot brought forward by those records. Such a body is FENCED — sealed
   *  under `snapshotAad`, which a build that does not know the rule cannot
   *  open. Absent, the body is the manifest at `manifestVersion`. The web
   *  never writes one. */
  snapshotVersion?: number;
  /** When the fenced body was written, by the server's clock. */
  snapshotWrittenAtMs?: number;
}

export interface CloudBlobEntry {
  /** Monotonic per blob; part of the blob's AAD. */
  version: number;
  /** sha256 of the PLAINTEXT file, hex — the diff key. */
  contentHash: string;
  sizeEnc: number;
}

/**
 * The in-memory model, path-keyed: `files` maps each vault path to the blob
 * holding its content. Under v2 several paths may share one blob (identical
 * content); v1 manifests are one-to-one by construction, so the inversion this
 * side does on read and write is lossless.
 */
export interface CloudManifest {
  blobs: Record<string, CloudBlobEntry>;
  files: Record<string, string>;
}

export const EMPTY_MANIFEST: CloudManifest = { blobs: {}, files: {} };

export interface OpenedManifest {
  manifest: CloudManifest;
  format: CloudManifestFormat;
}

/** A fenced body's AAD: the format's own, bound to the version the body holds. */
export function snapshotAad(format: CloudManifestFormat, version: number): Uint8Array {
  return utf8(`pondering.manifest.${format}.snapshot:${version}`);
}

/** Null on tamper, wrong key, or a body this version cannot parse. Tries v2
 *  first: a v1 body cannot open under the v2 AAD, and vice versa, so the order
 *  is a preference and never an ambiguity. `snapshotVersion` is the
 *  envelope's: set, only a body fenced at that version opens; absent, only an
 *  unfenced one does. */
export function openManifestBody(
  dataKey: Uint8Array,
  bodyBase64: string,
  snapshotVersion?: number,
): OpenedManifest | null {
  let sealed: Uint8Array;
  try {
    sealed = fromBase64(bodyBase64);
  } catch {
    return null;
  }
  const aad = (format: CloudManifestFormat) =>
    snapshotVersion !== undefined ? snapshotAad(format, snapshotVersion) : format === 'v2' ? MANIFEST_AAD_V2 : MANIFEST_AAD_V1;

  const v2 = open(dataKey, aad('v2'), sealed);
  if (v2) {
    try {
      const parsed = JSON.parse(fromUtf8(v2)) as { blobs?: Record<string, CloudBlobEntry>; files?: Record<string, string> };
      return { manifest: { blobs: parsed.blobs ?? {}, files: parsed.files ?? {} }, format: 'v2' };
    } catch {
      return null;
    }
  }

  const v1 = open(dataKey, aad('v1'), sealed);
  if (!v1) return null;
  try {
    const parsed = JSON.parse(fromUtf8(v1)) as { blobs?: Record<string, CloudBlobEntry>; paths?: Record<string, string> };
    const files: Record<string, string> = {};
    for (const [blobId, path] of Object.entries(parsed.paths ?? {})) files[path] = blobId;
    return { manifest: { blobs: parsed.blobs ?? {}, files }, format: 'v1' };
  } catch {
    return null;
  }
}

/** Sealed bytes, ready to upload as a body blob. Field order follows the
 *  Kotlin serializer's declaration order so a diff of two bodies stays
 *  readable; nothing depends on it, since both sides parse rather than
 *  compare. */
export function sealManifestBody(
  dataKey: Uint8Array,
  manifest: CloudManifest,
  format: CloudManifestFormat,
): Uint8Array {
  if (format === 'v2') {
    const ordered = { blobs: manifest.blobs, files: manifest.files };
    return seal(dataKey, MANIFEST_AAD_V2, utf8(JSON.stringify(ordered)));
  }
  // The inversion back to blobId → path is lossless only because v1 ids are
  // path-derived and therefore one-to-one; a v2 id can be shared, and is never
  // written into a v1 body.
  const paths: Record<string, string> = {};
  for (const [path, blobId] of Object.entries(manifest.files)) paths[blobId] = path;
  const ordered = { blobs: manifest.blobs, paths };
  return seal(dataKey, MANIFEST_AAD_V1, utf8(JSON.stringify(ordered)));
}

export function blobIdsUnder(manifest: CloudManifest, prefix: string): string[] {
  return Object.entries(manifest.files)
    .filter(([path]) => path.startsWith(prefix))
    .map(([, blobId]) => blobId);
}

export { MANIFEST_AAD_V1, MANIFEST_AAD_V2 };
